<template>
    <modal :show-title="true" size="large" @close="$emit('close')">
        <div slot="header">
            <h5 class="modal-title">{{ $t('feedback') }}</h5>
        </div>

        <div slot="body" class="feedback-modal">
            <div v-if="loading" class="text-center py-5">
                <div class="spinner-grow" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
            </div>

            <div v-else-if="!feedbackAvailable" class="text-center py-4">
                <span class="material-symbols-outlined icone50 color">sentiment_dissatisfied</span>
                <p class="mt-3 mb-0">{{ $t('feedback_non_disponibile') }}</p>
            </div>

            <div v-else style="max-height: 60vh; overflow-y: auto;">
                <div class="text-center pb-4" v-if="!isCompiled">
                    <span class="material-symbols-outlined color" style="font-size: 100px;">waving_hand</span>
                    <p class="mb-2 mt-2" style="font-weight: 500;">Ciao!</p>
                    <p class="mb-0">{{ $t('feedback_intro_text') }}</p>
                </div>

                <div class="mb-4 text-center" v-if="isCompiled">
                    <span class="material-symbols-outlined color" style="font-size: 100px;">cheer</span>
                    <p class="mt-2 mb-0" style="font-weight: 500;">{{ $t('feedback_inviato_successo') }}</p>
                    <br>{{ $t('feedback_intro_completed_text') }}
                    <p class="mb-0" v-if="formattedCompiledAt">
                        {{ $t('feedback_inviato_il') }} {{ formattedCompiledAt }}
                    </p>
                </div>

                <div class="mb-3 text-center" v-else>
                    <b>{{ $t('feedback_ordine_numero') }} {{ currentOrderId }}</b>
                    <p class="mb-0 mt-2" v-if="feedbackText">{{ feedbackText }}</p>
                </div>

                <div v-if="questions.length">
                    <div class="mb-4" v-for="(question, index) in questions" :key="question.id">
                        <div class="title mb-1"><b>{{ index + 1 }}. {{ getQuestionTitle(question) }}</b></div>
                        <div v-if="question.tipo === 'VALUTAZIONE_GRADIMENTO'" class="feedback-stars">
                            <button
                                type="button"
                                class="btn btn-link p-0 mr-1 feedback-star-btn"
                                v-for="n in 5"
                                :key="n"
                                :disabled="isCompiled"
                                @click="setRating(question, n)">
                                <span class="material-symbols-outlined feedback-star" :class="{'feedback-star-active': Number(getQuestionValue(question)) >= n}">
                                    {{ Number(getQuestionValue(question)) >= n ? 'star' : 'star_outline' }}
                                </span>
                            </button>
                        </div>

                        <div v-else-if="question.tipo === 'RISPOSTA_APERTA'">
                            <textarea
                                v-if="!isCompiled"
                                class="form-control"
                                rows="3"
                                :value="getQuestionValue(question)"
                                @input="setOpenAnswer(question, $event.target.value)"
                            />
                            <p v-else class="mb-0 feedback-readonly">{{ getQuestionValue(question) }}</p>
                        </div>

                        <div v-else-if="question.tipo === 'RISPOSTA_SI_NO'">
                            <div v-if="!isCompiled" class="feedback-yes-no">
                                <span>{{ $t('no') }}</span>
                                <label class="feedback-switch mb-0">
                                    <input
                                        type="checkbox"
                                        :checked="toBoolean(getQuestionValue(question))"
                                        @change="setYesNoAnswer(question, $event.target.checked)"
                                    >
                                    <span class="feedback-slider" />
                                </label>
                                <span>{{ $t('yes') }}</span>
                            </div>
                            <p v-else class="mb-0 feedback-readonly">
                                {{ toBoolean(getQuestionValue(question)) ? $t('yes') : $t('no') }}
                            </p>
                        </div>

                        <div v-else-if="question.tipo === 'RISPOSTA_SINGOLA'">
                            <div v-if="getAvailableAnswers(question).length">
                                <button
                                    type="button"
                                    class="btn btn-link p-0 d-flex align-items-center mb-2 feedback-option"
                                    :disabled="isCompiled"
                                    v-for="answer in getAvailableAnswers(question)"
                                    :key="answer.id"
                                    @click="setSingleAnswer(question, answer.id)">
                                    <span class="material-symbols-outlined mr-1">
                                        {{ isSingleSelected(question, answer.id) ? 'radio_button_checked' : 'radio_button_unchecked' }}
                                    </span>
                                    <span>{{ getAnswerText(answer) }}</span>
                                </button>
                            </div>
                            <p v-else class="mb-0 feedback-readonly">-</p>
                        </div>

                        <div v-else-if="question.tipo === 'RISPOSTA_MULTIPLA'">
                            <div v-if="getAvailableAnswers(question).length">
                                <button
                                    type="button"
                                    class="btn btn-link p-0 d-flex align-items-center mb-2 feedback-option"
                                    :disabled="isCompiled"
                                    v-for="answer in getAvailableAnswers(question)"
                                    :key="answer.id"
                                    @click="toggleMultipleAnswer(question, answer.id)">
                                    <span class="material-symbols-outlined mr-1">
                                        {{ isMultipleSelected(question, answer.id) ? 'check_box' : 'check_box_outline_blank' }}
                                    </span>
                                    <span>{{ getAnswerText(answer) }}</span>
                                </button>
                            </div>
                            <p v-else class="mb-0 feedback-readonly">-</p>
                        </div>
                    </div>
                </div>

                <p v-else class="text-center mb-0">{{ $t('feedback_no_questions') }}</p>
            </div>
        </div>

        <div slot="footer">
            <button
                v-if="!feedbackAvailable || isCompiled"
                type="button"
                class="btn btn-default"
                @click="$emit('close')">
                {{ $t('chiudi') }}
            </button>
            <button
                v-else
                type="button"
                class="btn btn-default"
                :disabled="sending || loading"
                @click="handleSave">
                {{ sending ? $t('attendi_attimo') : $t('feedback_invia') }}
            </button>
        </div>
    </modal>
</template>

<script>
import {mapState} from 'vuex';
import {getFeedback, saveFeedbackCompilato} from "../../../api/account";
import Modal from "../../partials/Modal";

export default {
    name: "ModalFeedback",
    components: {Modal},
    props: ['type', 'item'],
    data() {
        return {
            loading: false,
            sending: false,
            feedback: null,
            order: null,
            feedbackSendable: false,
            compiledAt: null,
            compiledFeedback: []
        }
    },
    created() {
        this.fetch();
    },
    computed: {
        ...mapState('localization', {
            language: state => state.language
        }),
        ...mapState('restaurant', {
            id_ristorante: state => state.id,
            locale: state => state.locale
        }),
        ...mapState('auth', {
            user: state => state
        }),
        feedbackAvailable: function () {
            return !!this.feedback && !!this.feedbackSendable;
        },
        isCompiled: function () {
            return !!this.compiledAt;
        },
        questions: function () {
            if (!this.feedback || !Array.isArray(this.feedback.feedback_domande)) {
                return [];
            }
            return [...this.feedback.feedback_domande].sort((a, b) => {
                const first = Number(a.ordine || 0);
                const second = Number(b.ordine || 0);
                return first - second;
            });
        },
        feedbackText: function () {
            return this.feedback && this.feedback.traduzione_ll ? this.feedback.traduzione_ll.testo : null;
        },
        currentOrderId: function () {
            if (this.order && this.order.id) {
                return this.order.id;
            }
            return this.item && this.item.id ? this.item.id : '-';
        },
        formattedCompiledAt: function () {
            if (!this.compiledAt) return null;
            return this.moment(this.compiledAt).locale(this.language).format('DD MMMM YYYY - HH:mm').toUpperCase();
        }
    },
    methods: {
        fetch: function () {
            if (!this.item || !this.item.id) {
                this.feedback = null;
                this.feedbackSendable = false;
                return;
            }

            this.loading = true;
            getFeedback(this.language, {
                id_ristorante: this.id_ristorante,
                id_locale: this.getLocaleId(),
                api_key: this.user.api_key,
                id_ordine: this.item.id
            }).then((response) => {
                this.feedback = response.feedback || null;
                this.order = response.ordine || null;
                this.feedbackSendable = !!response.feedback_sendable;

                if (response.feedback_compilato) {
                    this.compiledAt = response.feedback_compilato.data_ora || null;
                    this.compiledFeedback = this.normalizeCompiledFeedback(
                        response.feedback_compilato.feedback_compilato_risposte || []
                    );
                } else {
                    this.compiledAt = null;
                    this.compiledFeedback = [];
                    this.prefillYesNoAnswers();
                }
            }).catch(({messages}) => {
                alert(messages.join('\n'));
            }).finally(() => {
                this.loading = false;
            });
        },
        getLocaleId: function () {
            if (this.item && this.item.id_locale) return this.item.id_locale;
            if (this.order && this.order.id_locale) return this.order.id_locale;
            if (this.locale && this.locale.id) return this.locale.id;
            return null;
        },
        prefillYesNoAnswers: function () {
            if (!this.feedback || !Array.isArray(this.feedback.feedback_domande)) return;

            const updated = [...this.compiledFeedback];
            this.feedback.feedback_domande.forEach((question) => {
                if (question.tipo !== 'RISPOSTA_SI_NO') return;
                const alreadyPresent = updated.find((answer) => answer.id_question === question.id);
                if (!alreadyPresent) {
                    updated.push({
                        id_question: question.id,
                        type: question.tipo,
                        value: false
                    });
                }
            });
            this.compiledFeedback = updated;
        },
        normalizeCompiledFeedback: function (answers) {
            return answers.map((answer) => {
                return {
                    id_question: answer.id_question,
                    type: answer.type,
                    value: this.normalizeValueByType(answer.type, answer.value)
                };
            });
        },
        normalizeValueByType: function (type, value) {
            switch (type) {
                case 'RISPOSTA_SI_NO':
                    return this.toBoolean(value);
                case 'VALUTAZIONE_GRADIMENTO':
                    return Number(value) || 0;
                case 'RISPOSTA_SINGOLA':
                case 'RISPOSTA_MULTIPLA':
                    return this.toArray(value);
                case 'RISPOSTA_APERTA':
                default:
                    return value == null ? '' : String(value);
            }
        },
        toArray: function (value) {
            if (Array.isArray(value)) return value.map((item) => Number(item)).filter((item) => !isNaN(item));
            if (value == null || value === '') return [];
            if (typeof value === 'number') return [value];
            if (typeof value === 'string') {
                try {
                    const parsed = JSON.parse(value);
                    if (Array.isArray(parsed)) {
                        return parsed.map((item) => Number(item)).filter((item) => !isNaN(item));
                    }
                } catch (e) {}
                return value.split(',').map((item) => Number(item.trim())).filter((item) => !isNaN(item));
            }
            return [];
        },
        toBoolean: function (value) {
            return value === true || value === 1 || value === "1" || value === "true";
        },
        getQuestionTitle: function (question) {
            return question && question.traduzione_ll ? question.traduzione_ll.testo : '';
        },
        getQuestionValue: function (question) {
            const answer = this.compiledFeedback.find((item) => item.id_question === question.id);
            if (answer) return answer.value;

            switch (question.tipo) {
                case 'RISPOSTA_SI_NO':
                    return false;
                case 'VALUTAZIONE_GRADIMENTO':
                    return 0;
                case 'RISPOSTA_SINGOLA':
                case 'RISPOSTA_MULTIPLA':
                    return [];
                case 'RISPOSTA_APERTA':
                default:
                    return '';
            }
        },
        upsertAnswer: function (question, value) {
            const existingIndex = this.compiledFeedback.findIndex((item) => item.id_question === question.id);
            if (existingIndex >= 0) {
                const updated = [...this.compiledFeedback];
                updated[existingIndex] = {
                    ...updated[existingIndex],
                    value
                };
                this.compiledFeedback = updated;
            } else {
                this.compiledFeedback = [
                    ...this.compiledFeedback,
                    {
                        id_question: question.id,
                        type: question.tipo,
                        value
                    }
                ];
            }
        },
        setRating: function (question, rating) {
            if (this.isCompiled) return;
            this.upsertAnswer(question, rating);
        },
        setOpenAnswer: function (question, value) {
            if (this.isCompiled) return;
            this.upsertAnswer(question, value);
        },
        setYesNoAnswer: function (question, value) {
            if (this.isCompiled) return;
            this.upsertAnswer(question, !!value);
        },
        setSingleAnswer: function (question, answerId) {
            if (this.isCompiled) return;
            this.upsertAnswer(question, [answerId]);
        },
        toggleMultipleAnswer: function (question, answerId) {
            if (this.isCompiled) return;
            const current = this.toArray(this.getQuestionValue(question));
            const hasAnswer = current.includes(answerId);
            const updated = hasAnswer ? current.filter((id) => id !== answerId) : [...current, answerId];
            this.upsertAnswer(question, updated);
        },
        isSingleSelected: function (question, answerId) {
            return this.toArray(this.getQuestionValue(question)).includes(answerId);
        },
        isMultipleSelected: function (question, answerId) {
            return this.toArray(this.getQuestionValue(question)).includes(answerId);
        },
        getAvailableAnswers: function (question) {
            if (!question || !Array.isArray(question.feedback_risposte)) return [];
            return question.feedback_risposte;
        },
        getAnswerText: function (answer) {
            if (!answer) return '';
            if (answer.traduzione_ll && answer.traduzione_ll.testo) return answer.traduzione_ll.testo;
            return answer.testo || '';
        },
        isAnswered: function (question) {
            const value = this.getQuestionValue(question);
            switch (question.tipo) {
                case 'RISPOSTA_APERTA':
                    return String(value || '').trim().length > 0;
                case 'RISPOSTA_SI_NO':
                    return value === true || value === false || value === 1 || value === 0 || value === '1' || value === '0';
                case 'VALUTAZIONE_GRADIMENTO':
                    return Number(value) > 0;
                case 'RISPOSTA_SINGOLA':
                case 'RISPOSTA_MULTIPLA':
                    return this.toArray(value).length > 0;
                default:
                    return value !== null && value !== '';
            }
        },
        isFormComplete: function () {
            if (!this.questions.length) return false;
            return this.questions.every((question) => this.isAnswered(question));
        },
        normalizeValueForPayload: function (type, value) {
            switch (type) {
                case 'RISPOSTA_SI_NO':
                    return this.toBoolean(value);
                case 'VALUTAZIONE_GRADIMENTO':
                    return Number(value) || 0;
                case 'RISPOSTA_SINGOLA':
                case 'RISPOSTA_MULTIPLA':
                    return this.toArray(value);
                case 'RISPOSTA_APERTA':
                default:
                    return String(value || '').trim();
            }
        },
        handleSave: function () {
            if (this.sending || this.loading || this.isCompiled) return;
            if (!this.isFormComplete()) {
                alert(this.$t('feedback_domande_incomplete'));
                return;
            }

            const compiled = this.questions.map((question) => {
                return {
                    id_question: question.id,
                    type: question.tipo,
                    value: this.normalizeValueForPayload(question.tipo, this.getQuestionValue(question))
                };
            });

            this.sending = true;
            saveFeedbackCompilato(this.language, {
                id_ristorante: this.id_ristorante,
                id_locale: this.getLocaleId(),
                api_key: this.user.api_key,
                id_ordine: this.item.id,
                feedback_compilato: compiled
            }).then((response) => {
                this.compiledFeedback = compiled;
                this.compiledAt = response.feedback_compilato ? response.feedback_compilato.data_ora : this.moment().format('YYYY-MM-DD HH:mm:ss');
            }).catch(({messages}) => {
                alert(messages.join('\n'));
            }).finally(() => {
                this.sending = false;
            });
        }
    }
}
</script>

<style scoped>
.feedback-modal {
    min-height: 220px;
}

.feedback-star-btn {
    text-decoration: none !important;
}

.feedback-star {
    font-size: 40px;
    color: #a7a6ac;
}

.feedback-star-active {
    color: #f3b93f;
}

.feedback-readonly {
    color: #7f7f7f;
    white-space: pre-wrap;
}

.feedback-option {
    color: inherit;
    text-decoration: none !important;
}

.feedback-yes-no {
    display: flex;
    align-items: center;
    gap: 10px;
}

.feedback-switch {
    position: relative;
    display: inline-block;
    width: 46px;
    height: 24px;
}

.feedback-switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.feedback-slider {
    position: absolute;
    cursor: pointer;
    inset: 0;
    background-color: #ccc;
    transition: .2s;
    border-radius: 24px;
}

.feedback-slider:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: .2s;
    border-radius: 50%;
}

.feedback-switch input:checked + .feedback-slider {
    background-color: #8bd69e;
}

.feedback-switch input:checked + .feedback-slider:before {
    transform: translateX(22px);
}
</style>
